import { useApi } from './api';
import { URLS } from '../constants/urls';

export function useParticipanteService() {
    const { publicFetch, authenticatedFetch } = useApi();

    const ensureMiniTestePayload = (payload = {}) => {
        const idModulo = payload?.idModulo;
        if (!idModulo) {
            throw new Error('idModulo é obrigatório para consultar respostas do mini teste.');
        }
        return { idModulo };
    };

    const postMiniTesteRead = async (url, payload, token) => {
        const safePayload = ensureMiniTestePayload(payload);
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

        const response = await authenticatedFetch(url, {
            method: 'POST',
            ...(headers ? { headers } : {}),
            body: JSON.stringify(safePayload),
        });

        let data = null;
        try {
            data = await response.json();
        } catch {
            data = null;
        }

        if (!response.ok) {
            throw new Error(data?.message || 'Falha ao consultar respostas do mini teste.');
        }

        if (!data?.success) {
            throw new Error(data?.message || 'Não foi possível consultar respostas do mini teste.');
        }

        return data;
    };

    const parseResponseJson = async (response) => {
        try {
            return await response.json();
        } catch {
            return null;
        }
    };

    const postParticipanteModuloTesteResposta = async (url, payload = {}, token, fallbackErrorMessage) => {
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
        const response = await authenticatedFetch(url, {
            method: 'POST',
            ...(headers ? { headers } : {}),
            body: JSON.stringify(payload),
        });

        const data = await parseResponseJson(response);
        if (!response.ok) {
            throw new Error(data?.message || fallbackErrorMessage || 'Falha ao comunicar com o servidor.');
        }
        if (!data?.success) {
            throw new Error(data?.message || fallbackErrorMessage || 'Nao foi possivel concluir a operacao.');
        }

        return data;
    };

    const parseJsonLoose = (raw) => {
        if (raw === null || typeof raw === 'undefined') return null;
        if (typeof raw === 'string') {
            const trimmed = raw.trim();
            if (!trimmed) return null;
            try { return JSON.parse(trimmed); } catch { return null; }
        }
        if (typeof raw === 'object') return raw;
        return null;
    };

    const normalizeParticipanteModuloTesteRespostaRecord = (record) => {
        if (!record || typeof record !== 'object') return record;
        const raw =
            record?.respostas_json ??
            record?.respostasJson ??
            record?.respostas ??
            null;
        const parsed = parseJsonLoose(raw);
        return {
            ...record,
            respostas_json: parsed ?? raw,
            respostasJson: parsed ?? raw,
            respostas: parsed ?? raw,
        };
    };

    const normalizeParticipanteModuloTesteRespostaData = (result) => {
        if (!result || typeof result !== 'object') return result;
        const data = result?.data;
        if (Array.isArray(data)) {
            return { ...result, data: data.map(normalizeParticipanteModuloTesteRespostaRecord) };
        }
        if (data && typeof data === 'object') {
            return { ...result, data: normalizeParticipanteModuloTesteRespostaRecord(data) };
        }
        return result;
    };

    const criarParticipante = async (dados) => {
        const response = await publicFetch(URLS.CREATE_PARTICIPANTE, {
            method: 'POST',
            body: JSON.stringify(dados),
        });

        return response.json();
    };

    const atualizarParticipante = async (dados) => {
        const response = await authenticatedFetch(URLS.UPDATE_PARTICIPANTE, {
            method: 'POST',
            body: JSON.stringify(dados),
        });

        return response.json();
    };

    const geraCodigoRecuperacaoParticipante = async (dados) => {
        const response = await publicFetch(URLS.GEN_COD_RECUPERACAO, {
            method: 'POST',
            body: JSON.stringify(dados),
        });

        return response.json();
    };

    const atualizarSenhaParticipante = async (dados) => {
        const response = await publicFetch(URLS.VER_COD_RECUPERACAO, {
            method: 'POST',
            body: JSON.stringify(dados),
        });

        return response.json();
    };

    const deletarParticipante = async (id) => {
        const response = await authenticatedFetch(URLS.DELETE_PARTICIPANTE, {
            method: 'POST',
            body: JSON.stringify({ id }),
        });

        return response.json();
    };

    const listarParticipantes = async () => {
        const response = await authenticatedFetch(URLS.READ_ALL_PARTICIPANTE);

        return response.json();
    };

    const obterParticipantePorId = async (id) => {
        const response = await authenticatedFetch(URLS.READ_PARTICIPANTE, {
            method: 'POST',
            body: JSON.stringify({ id }),
        });

        return response.json();
    };

    const obterParticipantePorCelular = async (celular) => {
        const response = await publicFetch(URLS.READ_BY_CELULAR_PARTICIPANTE, {
            method: 'POST',
            body: JSON.stringify({ celular }),
        });

        return response.json();
    };

    const listarParticipantesPorTurma = async (idTurma) => {
        const response = await authenticatedFetch(URLS.READ_ALL_PARTICIPANTE_BY_TURMA, {
            method: 'POST',
            body: JSON.stringify({ idTurma }),
        });

        return response.json();
    };

    const listarParticipanteTurmasPorParticipante = async (idParticipante) => {
        const response = await authenticatedFetch(URLS.READ_PARTICIPANTE_TURMA_BY_PARTICIPANTE, {
            method: 'POST',
            body: JSON.stringify({ idParticipante }),
        });

        return response.json();
    };

    const obterParticipanteTurmaPorParticipanteETurma = async (idTurma) => {
        const response = await authenticatedFetch(URLS.READ_PARTICIPANTE_TURMA_BY_PARTICIPANTE_AND_TURMA, {
            method: 'POST',
            body: JSON.stringify({ idTurma }),
        });

        return response.json();
    };

    const readModuloMiniTesteRespostasByModuloUsuario = async (payload, token) => {
        return postMiniTesteRead(URLS.READ_MODULO_MINI_TESTE_RESPOSTAS_BY_MODULO_USUARIO, payload, token);
    };

    const readModuloMiniTesteRespostaUltimaTentativa = async (payload, token) => {
        return postMiniTesteRead(URLS.READ_MODULO_MINI_TESTE_RESPOSTA_ULTIMA_TENTATIVA, payload, token);
    };

    const createParticipanteModuloTesteResposta = async (payload, token) => {
        const result = await postParticipanteModuloTesteResposta(
            URLS.CREATE_PARTICIPANTE_MODULO_TESTE_RESPOSTA,
            payload,
            token,
            'Nao foi possivel criar a resposta do teste externo.'
        );
        return normalizeParticipanteModuloTesteRespostaData(result);
    };

    const upsertParticipanteModuloTesteResposta = async (payload, token) => {
        const result = await postParticipanteModuloTesteResposta(
            URLS.UPSERT_PARTICIPANTE_MODULO_TESTE_RESPOSTA,
            payload,
            token,
            'Nao foi possivel salvar a resposta do teste externo.'
        );
        return normalizeParticipanteModuloTesteRespostaData(result);
    };

    const updateParticipanteModuloTesteResposta = async (payload, token) => {
        const result = await postParticipanteModuloTesteResposta(
            URLS.UPDATE_PARTICIPANTE_MODULO_TESTE_RESPOSTA,
            payload,
            token,
            'Nao foi possivel atualizar a resposta do teste externo.'
        );
        return normalizeParticipanteModuloTesteRespostaData(result);
    };

    const deleteParticipanteModuloTesteResposta = async (payload, token) => {
        const body = typeof payload === 'string' ? { id: payload } : (payload || {});
        const result = await postParticipanteModuloTesteResposta(
            URLS.DELETE_PARTICIPANTE_MODULO_TESTE_RESPOSTA,
            body,
            token,
            'Nao foi possivel excluir a resposta do teste externo.'
        );
        return normalizeParticipanteModuloTesteRespostaData(result);
    };

    const readParticipanteModuloTesteRespostaById = async (payload, token) => {
        const body = typeof payload === 'string' ? { id: payload } : (payload || {});
        const result = await postParticipanteModuloTesteResposta(
            URLS.READ_PARTICIPANTE_MODULO_TESTE_RESPOSTA,
            body,
            token,
            'Nao foi possivel consultar a resposta do teste externo.'
        );
        return normalizeParticipanteModuloTesteRespostaData(result);
    };

    const readAllParticipanteModuloTesteResposta = async (payload = {}, token) => {
        const result = await postParticipanteModuloTesteResposta(
            URLS.READ_ALL_PARTICIPANTE_MODULO_TESTE_RESPOSTA,
            payload,
            token,
            'Nao foi possivel consultar todas as respostas de teste externo.'
        );
        return normalizeParticipanteModuloTesteRespostaData(result);
    };

    const readParticipanteModuloTesteRespostaByParticipante = async (payload = {}, token) => {
        const result = await postParticipanteModuloTesteResposta(
            URLS.READ_PARTICIPANTE_MODULO_TESTE_RESPOSTAS_BY_PARTICIPANTE,
            payload,
            token,
            'Nao foi possivel consultar respostas do participante.'
        );
        return normalizeParticipanteModuloTesteRespostaData(result);
    };

    const readParticipanteModuloTesteRespostaByParticipanteAndTeste = async (payload, token) => {
        const result = await postParticipanteModuloTesteResposta(
            URLS.READ_PARTICIPANTE_MODULO_TESTE_RESPOSTAS_BY_PARTICIPANTE_AND_TESTE,
            payload,
            token,
            'Nao foi possivel consultar respostas do participante no teste.'
        );
        return normalizeParticipanteModuloTesteRespostaData(result);
    };

    const readParticipanteModuloTesteRespostaByParticipanteAndModulo = async (payload, token) => {
        const result = await postParticipanteModuloTesteResposta(
            URLS.READ_PARTICIPANTE_MODULO_TESTE_RESPOSTAS_BY_PARTICIPANTE_AND_MODULO,
            payload,
            token,
            'Nao foi possivel consultar respostas do participante no modulo.'
        );
        return normalizeParticipanteModuloTesteRespostaData(result);
    };

    const readParticipanteModuloTesteRespostaByParticipanteAndTrilha = async (payload, token) => {
        const result = await postParticipanteModuloTesteResposta(
            URLS.READ_PARTICIPANTE_MODULO_TESTE_RESPOSTAS_BY_PARTICIPANTE_AND_TRILHA,
            payload,
            token,
            'Nao foi possivel consultar respostas do participante na trilha.'
        );
        return normalizeParticipanteModuloTesteRespostaData(result);
    };

    const readParticipanteModuloTesteRespostaByTurmaAndParticipante = async (payload, token) => {
        const result = await postParticipanteModuloTesteResposta(
            URLS.READ_PARTICIPANTE_MODULO_TESTE_RESPOSTAS_BY_TURMA_AND_PARTICIPANTE,
            payload,
            token,
            'Nao foi possivel consultar respostas por turma e participante.'
        );
        return normalizeParticipanteModuloTesteRespostaData(result);
    };

    // Aliases para compatibilidade com chamadas antigas do front.
    const createRespostaTrilhaTeste = createParticipanteModuloTesteResposta;
    const upsertRespostaTrilhaTeste = upsertParticipanteModuloTesteResposta;
    const updateRespostaTrilhaTeste = updateParticipanteModuloTesteResposta;
    const deleteRespostaTrilhaTeste = deleteParticipanteModuloTesteResposta;
    const readRespostaById = readParticipanteModuloTesteRespostaById;
    const readRespostasByParticipante = readParticipanteModuloTesteRespostaByParticipante;
    const readRespostasByParticipanteAndTeste = readParticipanteModuloTesteRespostaByParticipanteAndTeste;
    const readRespostasByParticipanteAndModulo = readParticipanteModuloTesteRespostaByParticipanteAndModulo;
    const readRespostasByParticipanteAndTrilha = readParticipanteModuloTesteRespostaByParticipanteAndTrilha;
    const readRespostasByTurmaAndParticipante = readParticipanteModuloTesteRespostaByTurmaAndParticipante;

    const listarTurmasTestePendentes = async (dados) => {
        const response = await authenticatedFetch(URLS.READ_PARTICIPANTE_TURMAS_TESTE_PENDENTES, {
            method: 'POST',
            body: JSON.stringify(dados),
        });

        return response.json();
    };

    return {
        criarParticipante,
        atualizarParticipante,
        geraCodigoRecuperacaoParticipante,
        atualizarSenhaParticipante,
        deletarParticipante,
        listarParticipantes,
        obterParticipantePorId,
        obterParticipantePorCelular,
        listarParticipantesPorTurma,
        listarParticipanteTurmasPorParticipante,
        obterParticipanteTurmaPorParticipanteETurma,
        readModuloMiniTesteRespostasByModuloUsuario,
        readModuloMiniTesteRespostaUltimaTentativa,
        createParticipanteModuloTesteResposta,
        upsertParticipanteModuloTesteResposta,
        updateParticipanteModuloTesteResposta,
        deleteParticipanteModuloTesteResposta,
        readParticipanteModuloTesteRespostaById,
        readAllParticipanteModuloTesteResposta,
        readParticipanteModuloTesteRespostaByParticipante,
        readParticipanteModuloTesteRespostaByParticipanteAndTeste,
        readParticipanteModuloTesteRespostaByParticipanteAndModulo,
        readParticipanteModuloTesteRespostaByParticipanteAndTrilha,
        readParticipanteModuloTesteRespostaByTurmaAndParticipante,
        createRespostaTrilhaTeste,
        upsertRespostaTrilhaTeste,
        updateRespostaTrilhaTeste,
        deleteRespostaTrilhaTeste,
        readRespostaById,
        readRespostasByParticipante,
        readRespostasByParticipanteAndTrilha,
        readRespostasByParticipanteAndModulo,
        readRespostasByParticipanteAndTeste,
        readRespostasByTurmaAndParticipante,
        listarTurmasTestePendentes,
    };
}
